import { useQueryClient } from "@tanstack/react-query";
import { getRouteApi, useNavigate } from "@tanstack/react-router";
import { Badge, TextField, type BadgeProps, type ListColumn } from "@wilco/ui";
import { DocumentList } from "../docs/DocumentList.tsx";
import { FormPage, useDocumentForm } from "../docs/DocumentForm.tsx";
import { serverPage } from "../docs/serverPage.ts";
import { formatDate, formatNumber, t, type MessageKey } from "../i18n.ts";
import { serviceApi, type QuarantineRow, type Repair, type RepairRow, type RepairState, type RepairWorkRow, type WorkState } from "./api.ts";

/*
 * «Операції» раздела «Ремонт» (handoff 3 ноября): ремонты списком,
 * новый ремонт по серийнику, работы всех ремонтов и «Карантин», откуда
 * «Сервіс» заводит ремонт. Списки страницами сервера (CursorPage):
 * фильтры и поиск считает он, порядок у каждого списка один.
 */

const SERVICE = ["service"] as const;

const REPAIR_STATE: Record<RepairState, { label: MessageKey; tone: BadgeProps["tone"] }> = {
  CONFIRMED: { label: "repair.state.CONFIRMED", tone: "info" },
  UNDER_REPAIR: { label: "repair.state.UNDER_REPAIR", tone: "strong" },
  DONE: { label: "repair.state.DONE", tone: "success" },
  CANCELLED: { label: "repair.state.CANCELLED", tone: "neutral" },
};
const REPAIR_STATES = Object.keys(REPAIR_STATE) as RepairState[];

const WORK_STATE: Record<WorkState, { label: MessageKey; tone: BadgeProps["tone"] }> = {
  TODO: { label: "step.TODO", tone: "neutral" },
  IN_PROGRESS: { label: "step.PROGRESS", tone: "strong" },
  PAUSED: { label: "step.PAUSED", tone: "warning" },
  DONE: { label: "step.DONE", tone: "success" },
  SKIPPED: { label: "step.SKIPPED", tone: "neutral" },
  REJECTED: { label: "step.REJECTED", tone: "danger" },
  CANCELLED: { label: "step.CANCELLED", tone: "neutral" },
};
const WORK_STATES = Object.keys(WORK_STATE) as WorkState[];

const asRepairState = (s: string | null): RepairState | null => (REPAIR_STATES.includes(s as RepairState) ? (s as RepairState) : null);
const asWorkState = (s: string | null): WorkState | null => (WORK_STATES.includes(s as WorkState) ? (s as WorkState) : null);

const repairsRoute = getRouteApi("/app/service/repairs");

export function RepairsPage() {
  const search = repairsRoute.useSearch();
  const navigate = useNavigate();
  const columns: ListColumn<RepairRow>[] = [
    { key: "number", header: t("repair.number"), kind: "title", width: "9rem", render: (r) => <span className="w-num">{r.number}</span> },
    { key: "serial", header: t("repair.serial"), kind: "text", width: "10rem", render: (r) => <span className="w-num">{r.serial}</span> },
    { key: "model", header: t("repair.product"), kind: "text", render: (r) => r.model },
    { key: "state", header: t("docs.status"), kind: "text", width: "9rem", render: (r) => <StateBadge {...REPAIR_STATE[r.state as RepairState]} /> },
    { key: "created", header: t("repair.created"), kind: "number", width: "7rem", render: (r) => formatDate(r.createdOn) },
  ];
  return (
    <DocumentList
      title={t("repair.list")}
      icon="repair"
      queryKey={[...SERVICE, "repairs", "list"]}
      load={async (q) => serverPage(await serviceApi.repairs({ ...q, state: asRepairState(q.status) }))}
      columns={columns}
      rowKey={(r) => r.id}
      onOpen={(r) => void navigate({ to: "/service/repairs/$id", params: { id: r.id } })}
      statuses={REPAIR_STATES.map((s) => ({ value: s, label: t(REPAIR_STATE[s].label) }))}
      sorts={[{ value: "new", label: t("docs.sort.newest") }]}
      search={search}
      onSearch={(next) => void navigate({ to: "/service/repairs", search: next, replace: true })}
      searchHint={t("repair.searchHint")}
      create={{ label: t("repair.new"), to: "/service/repairs/new" }}
      empty={{ title: t("repair.empty"), body: t("repair.emptyBody") }}
    />
  );
}

const newRepairRoute = getRouteApi("/app/service/repairs/new");

interface NewRepair {
  serial: string;
  reason: string;
}

/** Новый ремонт по серийнику с «Карантину» (SRV-01): серийник из «Карантину» приходит в адресе. */
export function NewRepairPage() {
  const { serial = "" } = newRepairRoute.useSearch();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const form = useDocumentForm<NewRepair, Repair>({
    initial: { serial, reason: "" },
    validate: (v) => ({ serial: v.serial.trim() ? undefined : t("docs.required") }),
    submit: (v, key) => serviceApi.open(v.serial.trim(), v.reason.trim() || null, key),
    onSuccess: (r) => {
      void queryClient.invalidateQueries({ queryKey: SERVICE });
      void navigate({ to: "/service/repairs/$id", params: { id: r.id }, replace: true });
    },
    // Отказ про серийник (незнакомый, не на карантине, ремонт уже открыт) под его полем.
    fieldOf: (code) => (code.startsWith("repair.") || code === "service.not_found" ? "serial" : undefined),
  });
  const { values, set, errors } = form;
  return (
    <FormPage title={t("repair.new")} back="/service/repairs" submitLabel={t("repair.open")} form={form}>
      <TextField label={t("repair.serial")} value={values.serial} onChange={(e) => set("serial", e.target.value)} error={errors.serial} autoComplete="off" autoFocus={!serial} />
      <TextField label={t("repair.reason")} value={values.reason} onChange={(e) => set("reason", e.target.value)} />
      <p className="w-muted">{t("repair.newHint")}</p>
    </FormPage>
  );
}

const worksRoute = getRouteApi("/app/service/works");

/** Работы всех ремонтов, каждая попытка строкой (SRV-02): строка ведёт в ремонт. */
export function RepairWorksPage() {
  const search = worksRoute.useSearch();
  const navigate = useNavigate();
  const columns: ListColumn<RepairWorkRow>[] = [
    { key: "name", header: t("repair.work"), kind: "title", render: (w) => w.name },
    { key: "repair", header: t("repair.number"), kind: "text", width: "9rem", render: (w) => <span className="w-num">{w.repairNumber}</span> },
    { key: "serial", header: t("repair.serial"), kind: "text", width: "10rem", render: (w) => <span className="w-num">{w.serial}</span> },
    { key: "state", header: t("docs.status"), kind: "text", width: "9rem", render: (w) => <StateBadge {...WORK_STATE[w.state as WorkState]} /> },
    { key: "attempt", header: t("repair.attempt"), kind: "number", width: "5rem", render: (w) => formatNumber(w.attempt) },
    { key: "minutes", header: t("repair.minutes"), kind: "number", width: "6rem", render: (w) => formatNumber(w.minutes) },
  ];
  return (
    <DocumentList
      title={t("repair.worksList")}
      icon="repair"
      queryKey={[...SERVICE, "works", "list"]}
      load={async (q) => serverPage(await serviceApi.repairWorks({ ...q, state: asWorkState(q.status) }))}
      columns={columns}
      rowKey={(w) => String(w.opId)}
      onOpen={(w) => void navigate({ to: "/service/repairs/$id", params: { id: w.repairId } })}
      statuses={WORK_STATES.map((s) => ({ value: s, label: t(WORK_STATE[s].label) }))}
      sorts={[{ value: "new", label: t("docs.sort.newest") }]}
      search={search}
      onSearch={(next) => void navigate({ to: "/service/works", search: next, replace: true })}
      searchHint={t("repair.worksSearchHint")}
      empty={{ title: t("repair.worksEmpty") }}
    />
  );
}

const quarantineRoute = getRouteApi("/app/service/quarantine");

/** «Карантин» склада «Ремонт» (решение C): из строки открыть ремонт или завести новый. */
export function QuarantinePage() {
  const search = quarantineRoute.useSearch();
  const navigate = useNavigate();
  const open = (u: QuarantineRow) =>
    u.openRepairId
      ? void navigate({ to: "/service/repairs/$id", params: { id: u.openRepairId } })
      : void navigate({ to: "/service/repairs/new", search: { serial: u.serial } });
  const columns: ListColumn<QuarantineRow>[] = [
    { key: "serial", header: t("repair.serial"), kind: "title", width: "11rem", render: (u) => <span className="w-num">{u.serial}</span> },
    { key: "model", header: t("repair.product"), kind: "text", render: (u) => u.model },
    { key: "assembled", header: t("repair.assembled"), kind: "number", width: "7rem", render: (u) => formatDate(u.assembledOn) },
    {
      key: "repair",
      header: t("repair.title"),
      kind: "text",
      width: "11rem",
      // Строка открывает ремонт или форму нового: здесь только что будет по нажатию.
      render: (u) => (u.openRepairNumber ? <span className="w-num">{u.openRepairNumber}</span> : <span className="w-muted">{t("repair.open")}</span>),
    },
  ];
  return (
    <DocumentList
      title={t("repair.quarantine")}
      icon="repair"
      queryKey={[...SERVICE, "quarantine", "list"]}
      load={async (q) => serverPage(await serviceApi.quarantine(q))}
      columns={columns}
      rowKey={(u) => u.serial}
      onOpen={open}
      sorts={[{ value: "serial", label: t("docs.sort.serial") }]}
      search={search}
      onSearch={(next) => void navigate({ to: "/service/quarantine", search: next, replace: true })}
      searchHint={t("repair.quarantineSearchHint")}
      empty={{ title: t("repair.quarantineEmpty"), body: t("repair.quarantineEmptyBody") }}
    />
  );
}

function StateBadge({ label, tone }: { label: MessageKey; tone: BadgeProps["tone"] }) {
  return <Badge tone={tone}>{t(label)}</Badge>;
}
